import React, { useEffect, useRef, useState } from "react";
import { resolveMediaUrl } from "../../../../shared/utils/mediaUtils";
import "./VideoPlayer.scss";

interface VideoPlayerProps {
	videoUrl?: string;
	posterUrl?: string;
	previewUrl?: string;
	width?: number;
	height?: number;
	mediaStatus?: "processing" | "ready" | "failed";
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
	videoUrl,
	posterUrl,
	previewUrl,
	width,
	height,
	mediaStatus,
}) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const previewRef = useRef<HTMLVideoElement>(null);
	const [showPreview, setShowPreview] = useState<boolean>(
		mediaStatus === "processing" && !!previewUrl
	);

	// Calculate aspect ratio for layout reservation
	const aspectRatio = width && height ? (height / width) * 100 : 56.25; // default 16:9

	const resolvedVideoUrl = resolveMediaUrl(videoUrl);
	const resolvedPosterUrl = resolveMediaUrl(posterUrl);
	const resolvedPreviewUrl = resolveMediaUrl(previewUrl);

	useEffect(() => {
		// Show preview while processing if available
		if (mediaStatus === "processing" && resolvedPreviewUrl) {
			setShowPreview(true);
			// Auto-play preview in loop
			if (previewRef.current) {
				previewRef.current.play().catch(() => {
					// Autoplay might be blocked
				});
			}
		} else if (mediaStatus === "ready") {
			setShowPreview(false);
		}
	}, [mediaStatus, resolvedPreviewUrl]);

	// Only render video player if media is ready
	const canPlayVideo = mediaStatus === "ready" && resolvedVideoUrl;

	return (
		<div className="video-player-container">
			{/* Reserve layout space to prevent shifts */}
			<div
				className="video-aspect-ratio"
				style={{ paddingBottom: `${aspectRatio}%` }}
			>
				{showPreview && resolvedPreviewUrl ? (
					// Show preview while processing
					<video
						ref={previewRef}
						className="video-preview"
						src={resolvedPreviewUrl}
						poster={resolvedPosterUrl}
						muted
						loop
						playsInline
					/>
				) : canPlayVideo ? (
					// Show main video when ready
					<video
						ref={videoRef}
						className="video-player"
						src={resolvedVideoUrl}
						poster={resolvedPosterUrl}
						controls
						preload="metadata"
						playsInline
					/>
				) : (
					// Show poster while waiting
					resolvedPosterUrl && (
						<img
							className="video-poster"
							src={resolvedPosterUrl}
							alt="Video thumbnail"
						/>
					)
				)}
			</div>
		</div>
	);
};

export default VideoPlayer;
